import {Injectable} from '@angular/core';
import {
  Auth,
  authState,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateEmail,
  updatePassword
} from '@angular/fire/auth';
import {
  addDoc,
  collection,
  doc,
  DocumentData,
  DocumentSnapshot,
  Firestore,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  Query,
  setDoc
} from '@angular/fire/firestore';
import {first, switchMap} from 'rxjs/operators';
import {BehaviorSubject, catchError, firstValueFrom, from, map, Observable, of, throwError} from "rxjs";
import {Router} from "@angular/router";
import {User} from "../models/user";

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  user$!: Observable<any>;
  loggedIn = new BehaviorSubject<boolean>(false);
  loggedIn$ = this.loggedIn.asObservable();
  linkedToOrganization = false;

  constructor(
    private auth: Auth,
    private firestore: Firestore,
    private router: Router
  ) {
    this.user$ = authState(auth).pipe(
      switchMap(user => {
        if (user) {
          this.loggedIn.next(true);
          const userDocRef = doc(firestore, `users/${user.uid}`);
          return new Observable((observer) => {
            const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
              observer.next(snapshot.data());
            });
            return { unsubscribe };
          });
        } else {
          this.loggedIn.next(false);
          return of(null);
        }
      })
    );

    // Checks if user is linked to an organization and sets boolean accordingly.
    this.user$.subscribe(data => {
      this.linkedToOrganization = (data != null && typeof data['organization'] == "object");
    });
  }


  /**
   * Creates a new user based on details filled in on registration form and returns newly generated user's uid.
   *
   * @param firstName
   * @param email
   * @param password
   * @return uid
   */
  async signUp({ firstName, email, password }: { firstName: string; email: string; password: string;}): Promise<any> {
    const credential = await createUserWithEmailAndPassword(
      this.auth,
      email,
      password
    );

    const uid = credential.user!.uid;
    const userEmail = credential.user!.email;

    const docRef = doc(this.firestore, "users", uid);
    await setDoc((docRef), {
      uid,
      email: userEmail,
      name: firstName,
      created: new Date().toLocaleDateString(),
      active: true,
      admin: false,
      superAdmin: false
    });

    return uid;
  }

  /**
   * Creates a new organization with user based on details filled in on registration form and returns newly generated user's uid.
   *
   * @param organizationName
   * @param address
   * @param contactName
   * @param contactPhone
   * @param email
   * @param password
   * @return uid
   */
  async signUpOrganization({ organization, address, contactName, contactPhone, email, password }: { organization: string; address: string; contactName: string; contactPhone: string; email: string; password: string;}): Promise<any> {
    const credential = await createUserWithEmailAndPassword(
      this.auth,
      email,
      password
    );

    const uid = credential.user!.uid;
    const userEmail = credential.user!.email;

    await addDoc(collection(this.firestore, 'organizations'), {
      name: organization,
      address,
      contactName,
      contactPhone,
      email,
      created: new Date().toLocaleDateString(),
      active: true
    }).then(docRef => {
      const ref = doc(this.firestore, "users", uid);
      setDoc((ref), {
        uid,
        email: userEmail,
        name: contactName,
        organization: docRef,
        created: new Date().toLocaleDateString(),
        active: true,
        admin: false,
        superAdmin: false
      });
    }).catch(error => console.error(error));

    return uid;
  }

  /**
   * Attempts to log in user with provided email and password.
   *
   * @param email
   * @param password
   * @return signed in user.
   */
  public signIn({ email, password }: { email: string; password: string; }) {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  /**
   * Attempts to re-authenticate the user and then update the users Firebase password.
   *
   * @param email
   * @param currentPassword
   * @param newPassword
   */
  public async updatePassword(email: string, currentPassword: string, newPassword: string) {
    await this.reauthenticateUser(email, currentPassword).then(async () => {
      await updatePassword(this.auth.currentUser!, newPassword).catch((error) => {
        throw error;
      });
    }).catch((error) => {
      throw error;
    });
  }

  /**
   * Attempts to re-authenticate the user and then update the users Firebase email address.
   *
   * @param email
   * @param newEmail
   * @param password
   */
  public async updateEmail(email: string, newEmail: string, password: string) {
    await this.reauthenticateUser(email, password).then(async () => {
      await updateEmail(this.auth.currentUser!, newEmail).then(async () => {
          await setDoc(doc(this.firestore, `users/${this.auth.currentUser?.uid}`), {
            email: newEmail
          }, {merge: true});
        }
      ).catch((error) => {
        throw error;
      });
    }).catch((error) => {
      throw error;
    });
  }

  /**
   * Redirects user to homepage if logged in, on pages like register/login.
   */
  public redirectLoggedInUser() {
    this.user$.subscribe(data => {
      if (data) {
        this.router.navigate(['/home']);
      }
    });
  }

  /**
   * Attempts to re-authenticate the user and returns UserCredential if successful.
   *
   * @param email
   * @param password
   */
  public async reauthenticateUser(email: string, password: string) {
    const credential = EmailAuthProvider.credential(email, password);

    try {
      return await reauthenticateWithCredential(this.auth.currentUser!, credential);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Returns the current user's information from this.user variable.
   *
   * @return all the information of user's firebase document.
   */
  public async getUser() {
    return await firstValueFrom(this.user$.pipe(first()));
  }

  /**
   * Retrieves user details in the user document on firebase.
   *
   * @param uid
   * @return document containing user details
   */
  public getUserDetails( uid: string ) {
    let docRef;

    if (uid.substring(0, 6) == 'users/') {
      docRef = doc(this.firestore, uid);
    } else {
      docRef = doc(this.firestore, `users/${uid}`);
    }

    return from(getDoc(docRef)).pipe(
      map((snapshot: DocumentSnapshot<DocumentData>) => {
        const data = snapshot.data();
        if (data) {
          return { data };
        } else {
          throw new Error(`Document with reference: ${uid} does not exist`);
        }
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * Updates user details in the user document on firebase.
   *
   * @param uid
   * @param username
   */
  public async changeUserDetails( uid: string, username: string ) {
    await setDoc(doc(this.firestore, `users/${uid}`), {
      name: username
    }, { merge: true });
  }

  /**
   * Updates a non-predetermined field in the user document on firebase.
   *
   * @param uid
   * @param field
   * @param value
   */
  public async updateField(uid: string, field: string, value: any) {
    await setDoc(doc(this.firestore, `users/${uid}`), {
      [field]: value
    }, { merge: true });
  }

  /**
   * Sends password reset email to provided email.
   *
   * @param email
   * @return Error upon failure.
   */
  public async sendPasswordResetEmail(email: string) {
    sendPasswordResetEmail(this.auth, email).catch((error) => {
      return error;
    });
  }

  /**
   * Signs out the user by calling the Firebase authentication function.
   */
  public async signOut() {
    signOut(this.auth).then(() => {
      this.router.navigate(['/account/inloggen']);
    });
  }

  /**
   * Counts the amount of documents in the users collection.
   */
  public async countUsers(): Promise<number> {
    const userQuery = query(
      collection(this.firestore, 'users')
    ) as Query<DocumentData>;

    try {
      const querySnapshot = await getDocs(userQuery);
      return querySnapshot.size;
    } catch (error) {
      console.error("Error executing query and counting results:", error);
      return 0;
    }
  }

  /**
   * Retrieves users from Firestore collection and returns them.
   *
   * @param offset
   * @return users
   */
  public async getUsers(offset: number): Promise<User[]> {
    const usersQuery = query(
      collection(this.firestore, "users"), limit(50)
    ) as Query<DocumentData>;

    const querySnapshot = await getDocs(usersQuery);
    return querySnapshot.docs.map(doc => ({
      uid: doc.id,
      ...doc.data()
    }) as User);
  }
}
